import gsap from 'gsap';
import { Color, LoopOnce, LoopRepeat, MeshPhongMaterial } from 'three';
import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';

const FADE_DURATION = 0.4;

export default class CharacterAnimationController {
    static _charMixer = null;
    static _taponMixer = null;
    static _base = null;
    static _tapon = null;
    static _taponAnims = [];
    static _taponScene = null;
    static _setMorph = null;
    static _morphNames = [];
    static _helmetDelayed = null;

    static _origCameraZ = 0;
    static _origCameraY = 0;

    // Tapon material
    static _taponMaterial = null;
    static _colorLuminaris = new Color('#F1F1F1');
    static _colorObscurios = new Color('#000000');

    // -------------------------------------------------------------------
    // Init
    // -------------------------------------------------------------------

    static init(charMixer, charClips, taponMixer = null, taponClips = [], opts = {}) {
        this._charMixer = charMixer;
        this._taponMixer = taponMixer;
        this._taponScene = opts.taponScene || null;
        this._setMorph = opts.setMorph || null;
        this._morphNames = opts.morphNames || [];

        // Character: _BASE en loop permanent
        const baseClip = charClips.find(c => c.name.endsWith('_BASE'));
        const taponClip = charClips.find(c => c.name.endsWith('_TAPON'));

        if (baseClip) {
            this._base = charMixer.clipAction(baseClip);
            this._base.setLoop(LoopRepeat);
            this._base.play();
        }

        if (taponClip) {
            this._tapon = charMixer.clipAction(taponClip);
            this._tapon.setLoop(LoopOnce);
            this._tapon.clampWhenFinished = true;
        }

        // Tapon.glb: inicialitza les actions però no les juga
        this._taponAnims = taponMixer
            ? taponClips.map(clip => {
                const a = taponMixer.clipAction(clip);
                a.setLoop(LoopOnce);
                a.clampWhenFinished = true;
                return a;
            })
            : [];

        // MeshPhongMaterial pels meshes del tapon (color configurable)
        this._taponMaterial = new MeshPhongMaterial({ color: this._colorLuminaris, shininess: 60 });
        if (this._taponScene) {
            this._taponScene.traverse(obj => {
                if (obj.isMesh) {
                    obj.material = this._taponMaterial;
                    obj.material.needsUpdate = true;
                }
            });
        }

        // Casc visible i HIDE CABESA = 0
        this._setTaponOpacity(1);
        const hideName = this._morphNames.find(n =>
            n.toLowerCase().includes('hide') ||
            n.toLowerCase().includes('cabesa') ||
            n.toLowerCase().includes('cabeza')
        );
        if (hideName && this._setMorph) this._setMorph(hideName, 0);
    }

    // -------------------------------------------------------------------
    // API pública
    // -------------------------------------------------------------------

    // Atura l'animació _BASE (Products page — el character és estàtic)
    static stopBase() {
        if (this._base) this._base.stop();
    }

    // Repren l'animació _BASE (Home page)
    static startBase() {
        if (this._base) {
            this._base.reset();
            this._base.setLoop(LoopRepeat);
            this._base.play();
        }
    }


    // Crossfade color del material del tapon: t=0 luminaris, t=1 obscurios
    static setTaponCrossFade(t) {
        if (!this._taponMaterial) return;
        this._taponMaterial.color.copy(this._colorLuminaris).lerp(this._colorObscurios, t);
    }

    // Actualitza els colors dels settings de scrollProgress (per debug pane)
    static setTaponColors(luminar, obscur) {
        this._colorLuminaris.set(luminar);
        this._colorObscurios.set(obscur);
    }

    // Posa _TAPON i les animacions del tapon.glb al frame final (per a pàgina Products)
    static setTaponToEnd() {
        if (this._tapon && this._charMixer) {
            const d = this._tapon.getClip().duration;
            this._tapon.reset().play();
            this._tapon.time = d;
            this._charMixer.update(0);
        }
        this._taponAnims.forEach(a => {
            if (!this._taponMixer) return;
            const d = a.getClip().duration;
            a.reset().play();
            a.time = d;
            this._taponMixer.update(0);
        });
    }

    // Posa _TAPON i les animacions del tapon.glb al frame inicial (per a pàgina Home)
    // i assegura HIDE CABESA = 0. paused = true congela el pose a frame 0 (el loop no avança).
    static setTaponToStart() {
        if (this._tapon && this._charMixer) {
            this._tapon.reset().play();
            this._tapon.time = 0;
            this._tapon.paused = true;
            this._charMixer.update(0);
        }
        this._taponAnims.forEach(a => {
            if (!this._taponMixer) return;
            a.reset().play();
            a.time = 0;
            a.paused = true;
            this._taponMixer.update(0);
        });

        // HIDE CABESA = 0
        const hideName = this._morphNames.find(n =>
            n.toLowerCase().includes('hide') ||
            n.toLowerCase().includes('cabesa') ||
            n.toLowerCase().includes('cabeza')
        );
        if (hideName && this._setMorph) this._setMorph(hideName, 0);

        // Estat inicial: tapon a opacity 0
        this._setTaponOpacity(0);
    }

    // PAS 1 (només opacity): el helmet es fa visible de cop (snap). Sense animació ni HIDE CABESA.
    static showHelmetSnap() {
        this._setTaponOpacity(1);
    }

    // Durada de l'animació tapon (clip _TAPON i anims del tapon.glb).
    static getTaponDuration() {
        let d = this._tapon ? this._tapon.getClip().duration : 0;
        this._taponAnims.forEach(a => { d = Math.max(d, a.getClip().duration); });
        return d;
    }

    // PAS 2: activa l'animació tapon (_TAPON + tapon.glb), atura l'idle i amaga el cap
    // (HIDE CABESA = 1, el helmet ja hi és). Retorna la durada de l'animació.
    static playTaponAnim() {
        if (this._tapon) this._tapon.reset().play();
        this._taponAnims.forEach(a => a.reset().play());

        if (this._base) this._base.stop();
        const hideName = this._morphNames.find(n =>
            n.toLowerCase().includes('hide') ||
            n.toLowerCase().includes('cabesa') ||
            n.toLowerCase().includes('cabeza')
        );
        if (hideName && this._setMorph) this._setMorph(hideName, 1);

        return this.getTaponDuration();
    }

    // Compat (WidgetTapon a la pàgina de producte): opacity 1 + play anims + HIDE CABESA amb delay.
    static showHelmet(delay = 2.5) {
        this.showHelmetSnap();
        if (this._tapon) this._tapon.reset().play();
        this._taponAnims.forEach(a => a.reset().play());
        if (this._helmetDelayed) this._helmetDelayed.kill();
        this._helmetDelayed = gsap.delayedCall(delay, () => {
            if (this._base) this._base.stop();
            const hideName = this._morphNames.find(n =>
                n.toLowerCase().includes('hide') ||
                n.toLowerCase().includes('cabesa') ||
                n.toLowerCase().includes('cabeza')
            );
            if (hideName && this._setMorph) this._setMorph(hideName, 1);
        });
    }

    // Inverteix showHelmet(): torna character + tapon a l'estat inicial de la Home
    static resetHelmet() {
        // Cancel·la el delayedCall pendent
        if (this._helmetDelayed) { this._helmetDelayed.kill(); this._helmetDelayed = null; }

        // Animacions a frame 0 + HIDE CABESA = 0 + tapon opacity 0
        this.setTaponToStart();

        // Reprèn l'idle (_BASE)
        this.startBase();
    }

    static _onCharFinished(e) {
        this._charMixer.removeEventListener('finished', this._onCharFinished);
    }

    static _onTaponFinished(e) {
        this._taponMixer?.removeEventListener('finished', this._onTaponFinished);
    }

    // -------------------------------------------------------------------
    // Camera
    // -------------------------------------------------------------------

    // Torna la càmera a la posició original (cridat quan es canvia de pàgina des de Home)
    static resetCamera(duration = 1.5) {
        const origZ = this._origCameraZ || WebGLSketch.cameraGroup.position.z;
        const origY = this._origCameraY || 0;
        gsap.killTweensOf(WebGLSketch.cameraGroup.position);
        gsap.to(WebGLSketch.cameraGroup.position, {
            z: origZ, y: origY, duration, ease: 'power2.inOut',
            onUpdate: () => {
                WebGLSketch.cameraPosition.z = WebGLSketch.cameraGroup.position.z;
                WebGLSketch.cameraPosition.y = WebGLSketch.cameraGroup.position.y;
            },
            onComplete: () => {
                this._origCameraZ = 0;
                this._origCameraY = 0;
            }
        });
    }

    static _cameraMoveTo(targetZ, duration, ease = 'power2.inOut') {
        if (!this._origCameraZ) this._origCameraZ = WebGLSketch.cameraGroup.position.z;
        gsap.killTweensOf(WebGLSketch.cameraGroup.position, 'z');
        gsap.to(WebGLSketch.cameraGroup.position, {
            z: targetZ, duration, ease,
            onUpdate: () => { WebGLSketch.cameraPosition.z = WebGLSketch.cameraGroup.position.z; },
            onComplete: () => { WebGLSketch.cameraPosition.z = targetZ; }
        });
    }

    static _cameraMoveY(targetY, duration, ease = 'power2.inOut') {
        if (!this._origCameraY) this._origCameraY = WebGLSketch.cameraGroup.position.y;
        gsap.killTweensOf(WebGLSketch.cameraGroup.position, 'y');
        gsap.to(WebGLSketch.cameraGroup.position, {
            y: targetY, duration, ease,
            onUpdate: () => { WebGLSketch.cameraPosition.y = WebGLSketch.cameraGroup.position.y; },
            onComplete: () => { WebGLSketch.cameraPosition.y = targetY; }
        });
    }

    // -------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------

    // IMPORTANT: ha de reflectir Scene._setTaponOpacity (mateix helmet). visible lligat a
    // l'opacity perquè a opacity 0 el mesh no escrigui depth (silueta) i, en tornar a
    // opacity 1 (showHelmet), es torni a veure. depthWrite = value >= 1: a opacity 1 el
    // helmet és opac i ESCRIU depth (si no, com que és transparent i el render és multipass,
    // es veu semitransparent); durant el fade (value < 1) NO escriu depth per no ocluir el
    // que va darrere en passes posteriors (thumbnails de Products).
    static _setTaponOpacity(value) {
        if (!this._taponScene) return;
        this._taponScene.traverse(obj => {
            if (obj.isMesh && obj.material) {
                obj.material.transparent = true;
                obj.material.opacity = value;
                obj.material.depthWrite = value >= 1;
                obj.visible = value > 0;
            }
        });
    }

    // -------------------------------------------------------------------
    // Loop / Dispose
    // -------------------------------------------------------------------

    static loop(delta) {
        if (this._charMixer) this._charMixer.update(delta);
        if (this._taponMixer) this._taponMixer.update(delta);
    }

    static dispose() {
        gsap.killTweensOf(WebGLSketch.cameraGroup?.position);
        if (WebGLSketch.cameraGroup) {
            if (this._origCameraZ) { WebGLSketch.cameraGroup.position.z = this._origCameraZ; WebGLSketch.cameraPosition.z = this._origCameraZ; }
            if (this._origCameraY) { WebGLSketch.cameraGroup.position.y = this._origCameraY; WebGLSketch.cameraPosition.y = this._origCameraY; }
        }
        if (this._charMixer) { this._charMixer.stopAllAction(); this._charMixer = null; }
        if (this._taponMixer) { this._taponMixer.stopAllAction(); this._taponMixer = null; }
        this._base = null;
        this._tapon = null;
        this._taponAnims = [];
        this._taponScene = null;
        this._setMorph = null;
        if (this._taponMaterial) { this._taponMaterial.dispose(); this._taponMaterial = null; }
    }
}
